/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * THE WEEK EDITOR'S TREE OPERATIONS (stage 1, 2026-10-19, `docs/WEEK-EDITOR-STAGE1-PLAN.md`). PURE — `weekEdit.test.ts`
 * executes every one.
 *
 * Every change the Week makes (a line, add, remove, swap, pair, unpair, move, copy) is computed HERE as the whole new tree
 * of each client copy it touches, IDS KEPT, and sent as ONE call (`write_week_trees`), which makes the database match. Undo
 * is the same call with the tree from before — the SAME ids come back (codex `data-and-sync`: "a save that replaces rows
 * re-mints every id, and whatever else held those ids is now keyed to nothing" — the review's next-week cells are keyed by
 * set id).
 *
 * 🔴 THE SUPERSET FLAG IS A BOUNDARY (`superset_with_next`, codex): moving or removing a row touches THREE flags — the row's
 * own, the one before where it LEFT (a last member leaving unjoins the row before it), and the one before where it LANDS (a
 * landing never splits a run: the place snaps to the run's end).
 * 🔴 PAIRING LEVELS DOWN (his rule, DECIDED "Unequal superset: the smaller count wins; the surplus is a trailing solo row") and
 * puts each member's warm-up sets on their own row BEFORE the run (his 2026-09-04 rung rule) — the builder's `resolveSupersets`
 * shape, done here with ids so nothing is re-minted.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */
import type { AssignedWorkoutTree, TreeExercise, TreeSection, TreeSet } from "./assignedWorkoutTree";
import { wireNumber } from "./wireNumber";
import { showLine, type LineFields, type LineSet } from "./exerciseLine";

export type WeekTree = AssignedWorkoutTree & { updated_at?: string | null };
export type NewId = () => string;

/* ── reading ──────────────────────────────────────────────────────────────────────────────────────────────────────── */

export function sectionsOf(t: WeekTree): TreeSection[] { return t.coach_workout_sections ?? []; }
export function exercisesOf(s: TreeSection): TreeExercise[] { return s.coach_workout_exercises ?? []; }
export function setsOf(e: TreeExercise): TreeSet[] { return e.coach_workout_sets ?? []; }

/** Where a row lives: its section and index. */
export function findRow(t: WeekTree, rowId: string): { section: TreeSection; index: number } | null {
  for (const section of sectionsOf(t)) {
    const index = exercisesOf(section).findIndex((e) => e.id === rowId);
    if (index !== -1) return { section, index };
  }
  return null;
}

/** The run (superset) a row sits in, as row ids in order; a single row is a run of one. */
export function runOf(t: WeekTree, rowId: string): string[] {
  const at = findRow(t, rowId);
  if (at === null) return [];
  const rows = exercisesOf(at.section);
  let start = at.index;
  while (start > 0 && rows[start - 1]?.superset_with_next === true) start -= 1;
  let end = at.index;
  while (end < rows.length - 1 && rows[end]?.superset_with_next === true) end += 1;
  return rows.slice(start, end + 1).map((r) => r.id);
}

/** The line's fields as the stored rows hold them. */
export function lineFieldsOf(e: TreeExercise): LineFields {
  const sets = setsOf(e);
  return {
    sets: sets.map((s) => ({
      type: (s.set_type ?? "regular") as LineSet["type"], reps: s.reps, weightKg: wireNumber(s.weight_kg),
      timeSeconds: s.time_seconds, distanceM: wireNumber(s.distance_m ?? null),
    })),
    restSeconds: sets[0]?.rest_seconds ?? null,
    tempo: e.tempo,
    eachSide: e.each_side === true,
    note: e.notes === null || e.notes.trim() === "" ? null : e.notes,
  };
}

/* ── writing helpers ──────────────────────────────────────────────────────────────────────────────────────────────── */

function mapSections(t: WeekTree, f: (s: TreeSection) => TreeSection): WeekTree {
  return { ...t, coach_workout_sections: sectionsOf(t).map(f) };
}

/** Positions 0.. in every section, sets and sections; a section a change EMPTIED is removed — implicit ones by §6.5's rule
 *  ("deleted when its last exercise leaves"), named ones because the builder refuses to save one ("This section has no
 *  exercises.", `validate`) and the phone would draw an empty card (Undo brings it back, ids and all); a flag on a section's
 *  last row cleared (it joins nothing — V6-12). */
export function tidy(t: WeekTree): WeekTree {
  const sections = sectionsOf(t)
    .filter((s) => exercisesOf(s).length > 0)
    .map((s, si) => {
      const rows = exercisesOf(s);
      return {
        ...s, position: si,
        coach_workout_exercises: rows.map((e, ei) => ({
          ...e, position: ei,
          superset_with_next: ei === rows.length - 1 ? false : e.superset_with_next === true,
          coach_workout_sets: setsOf(e).map((x, k) => ({ ...x, position: k })),
        })),
      };
    });
  return { ...t, coach_workout_sections: sections };
}

function setRow(id: string, s: LineSet, rest: number | null, keep?: TreeSet): TreeSet {
  return {
    id, position: 0, reps: s.reps, weight_kg: s.weightKg, time_seconds: s.timeSeconds, distance_m: s.distanceM,
    rounds: keep?.rounds ?? null, rest_seconds: rest, set_type: s.type,
  };
}

/* ── one row ──────────────────────────────────────────────────────────────────────────────────────────────────────── */

/** A typed line written onto a row: set rows kept by POSITION (ids too), extras new, surplus gone; tempo, side, note. */
export function applyLine(t: WeekTree, rowId: string, f: LineFields, newId: NewId): WeekTree {
  return tidy(mapSections(t, (s) => ({
    ...s,
    coach_workout_exercises: exercisesOf(s).map((e) => {
      if (e.id !== rowId) return e;
      const old = setsOf(e);
      const sets: TreeSet[] = f.sets === null
        ? old.map((x) => ({ ...x, rest_seconds: f.restSeconds }))
        : f.sets.map((ls, i) => setRow(old[i]?.id ?? newId(), ls, f.restSeconds, old[i]));
      return { ...e, tempo: f.tempo, each_side: f.eachSide, notes: f.note, coach_workout_sets: sets };
    }),
  })));
}

/** A library pick, seeded as the builder seeds one (`seededRow`): three sets, rest 90, the library's note. */
export type LibraryPick = { id: string; name: string; note: string | null };

/** The section a new or moved row goes to when no place is named: the LAST main-work section (a warm-up or a core block is
 *  not where a main lift lands), or a new implicit one at the end. */
function landingSection(t: WeekTree, newId: NewId, needsPairs: boolean): { tree: WeekTree; sectionId: string } {
  const all = sectionsOf(t);
  for (let i = all.length - 1; i >= 0; i -= 1) {
    const s = all[i]!;
    if ((s.section_type ?? "workout") !== "workout") continue;
    if (needsPairs && !pairsAllowed(s.format)) continue;
    return { tree: t, sectionId: s.id };
  }
  const id = newId();
  const section: TreeSection = {
    id, position: all.length, name: null, format: "straight_sets", section_type: "workout", format_config: null,
    description: null, is_implicit: true, coach_workout_exercises: [],
  };
  return { tree: { ...t, coach_workout_sections: [...all, section] }, sectionId: id };
}

/** The builder's `allowsSupersets` — every format but EMOM and for-time. */
export function pairsAllowed(format: string | null): boolean {
  return format === null || ["straight_sets", "superset", "circuit", "amrap", "timed_interval"].includes(format);
}

/** The builder's seed for a library pick (`seededRow`): three sets, rest 90, the library's note. */
function seededRow(pick: LibraryPick, newId: NewId): TreeExercise {
  return {
    id: newId(), exercise_id: pick.id, position: 0, each_side: false, hold_until_lost: false, tempo: null,
    notes: pick.note, superset_with_next: false, coach_exercises: { name: pick.name },
    coach_workout_sets: [0, 1, 2].map(() => ({
      id: newId(), position: 0, reps: null, weight_kg: null, time_seconds: null, rounds: null, rest_seconds: 90,
      set_type: "regular", distance_m: null,
    })),
  };
}

export function addExercise(t: WeekTree, pick: LibraryPick, newId: NewId): { tree: WeekTree; rowId: string } {
  const { tree, sectionId } = landingSection(t, newId, false);
  const row = seededRow(pick, newId);
  const rowId = row.id;
  return {
    tree: tidy(mapSections(tree, (s) => (s.id === sectionId ? { ...s, coach_workout_exercises: [...exercisesOf(s), row] } : s))),
    rowId,
  };
}

export function swapExercise(t: WeekTree, rowId: string, pick: LibraryPick): WeekTree {
  return mapSections(t, (s) => ({
    ...s,
    coach_workout_exercises: exercisesOf(s).map((e) => (e.id === rowId ? { ...e, exercise_id: pick.id, coach_exercises: { name: pick.name } } : e)),
  }));
}

/** Rows taken out of a tree, with the flag before them fixed: when the LAST member of a run leaves, the row before it no
 *  longer joins anything. (A middle member leaving keeps its neighbours joined — A–B–C less B is A–C.) */
function takeRows(t: WeekTree, ids: readonly string[]): { tree: WeekTree; rows: TreeExercise[] } {
  const want = new Set(ids);
  const rows: TreeExercise[] = [];
  const tree = mapSections(t, (s) => {
    const list = exercisesOf(s);
    const kept: TreeExercise[] = [];
    list.forEach((e) => {
      if (!want.has(e.id)) { kept.push(e); return; }
      rows.push(e);
      const prev = kept[kept.length - 1];
      if (prev !== undefined && prev.superset_with_next === true && e.superset_with_next !== true) {
        kept[kept.length - 1] = { ...prev, superset_with_next: false };
      }
    });
    return { ...s, coach_workout_exercises: kept };
  });
  return { tree, rows };
}

export function removeRow(t: WeekTree, rowId: string): WeekTree {
  return tidy(takeRows(t, [rowId]).tree);
}

/* ── supersets ────────────────────────────────────────────────────────────────────────────────────────────────────── */

/** Join a row's run with the run after it, then level the new run (rungs before it, surplus after it). Null when there is
 *  no next row in the section or the section's format takes no pairs. */
export function pairWithNext(t: WeekTree, rowId: string, newId: NewId): WeekTree | null {
  const at = findRow(t, rowId);
  if (at === null || !pairsAllowed(at.section.format)) return null;
  const run = runOf(t, rowId);
  const rows = exercisesOf(at.section);
  const lastIdx = rows.findIndex((r) => r.id === run[run.length - 1]);
  if (lastIdx === -1 || lastIdx >= rows.length - 1) return null;
  const joined = rows.map((r, i) => (i === lastIdx ? { ...r, superset_with_next: true } : r));
  const section = levelRun({ ...at.section, coach_workout_exercises: joined }, rows[lastIdx]!.id, newId);
  return tidy(mapSections(t, (s) => (s.id === at.section.id ? section : s)));
}

/** Break the whole run a row sits in into single rows. */
export function unpair(t: WeekTree, rowId: string): WeekTree {
  const run = new Set(runOf(t, rowId));
  return tidy(mapSections(t, (s) => ({
    ...s, coach_workout_exercises: exercisesOf(s).map((e) => (run.has(e.id) ? { ...e, superset_with_next: false } : e)),
  })));
}

/** The run containing `anyMember` levelled: each member's warm-up sets on a solo row BEFORE the run (member order), the
 *  members cut to the shortest working count, the surplus on solo rows AFTER the run. Set ids travel with their sets. */
function levelRun(section: TreeSection, anyMember: string, newId: NewId): TreeSection {
  const rows = exercisesOf(section);
  const idx = rows.findIndex((r) => r.id === anyMember);
  let start = idx, end = idx;
  while (start > 0 && rows[start - 1]?.superset_with_next === true) start -= 1;
  while (end < rows.length - 1 && rows[end]?.superset_with_next === true) end += 1;
  const members = rows.slice(start, end + 1);
  if (members.length < 2) return section;
  const solo = (e: TreeExercise, sets: TreeSet[]): TreeExercise => ({ ...e, id: newId(), superset_with_next: false, coach_workout_sets: sets });
  const before: TreeExercise[] = [];
  const split = members.map((e) => {
    const sets = setsOf(e);
    const rungs = sets.filter((x) => x.set_type === "warm_up");
    const work = sets.filter((x) => x.set_type !== "warm_up");
    if (rungs.length > 0 && work.length > 0) { before.push(solo(e, rungs)); return { ...e, coach_workout_sets: work }; }
    return e;
  });
  const n = Math.min(...split.map((e) => setsOf(e).length));
  const after: TreeExercise[] = [];
  const levelled = split.map((e, i) => {
    const sets = setsOf(e);
    const joinsNext = i < split.length - 1;
    if (sets.length > n) after.push(solo(e, sets.slice(n)));
    return { ...e, superset_with_next: joinsNext, coach_workout_sets: sets.slice(0, n) };
  });
  return { ...section, coach_workout_exercises: [...rows.slice(0, start), ...before, ...levelled, ...after, ...rows.slice(end + 1)] };
}

/* ── moving and copying between client copies ────────────────────────────────────────────────────────────────────── */

/** Where rows land: a workout, and optionally a section + the index in it, or (a drag) the row they land AFTER — read once the
 *  moving rows are out, so a move down its own section is never off by its own size; `afterRowId: null` + `sectionId` = the
 *  section's top. None of these = the end (`landingSection`). */
export type Landing = { workoutId: string; sectionId?: string; index?: number; afterRowId?: string | null };

/** Snap an index to a run boundary, so a landing never splits a superset. */
function snapped(rows: readonly TreeExercise[], index: number): number {
  let i = Math.max(0, Math.min(index, rows.length));
  while (i > 0 && i < rows.length && rows[i - 1]?.superset_with_next === true) i += 1;
  return i;
}

function place(t: WeekTree, rows: TreeExercise[], at: Landing, newId: NewId): WeekTree {
  let tree = t;
  if (typeof at.afterRowId === "string") {
    const where = findRow(tree, at.afterRowId);
    at = where === null ? { workoutId: at.workoutId } : { workoutId: at.workoutId, sectionId: where.section.id, index: where.index + 1 };
  } else if (at.afterRowId === null) {
    at = { workoutId: at.workoutId, sectionId: at.sectionId, index: 0 };
  }
  let sectionId = at.sectionId;
  const pairs = rows.some((r) => r.superset_with_next === true);
  if (sectionId === undefined || !sectionsOf(tree).some((s) => s.id === sectionId && (!pairs || pairsAllowed(s.format)))) {
    const l = landingSection(tree, newId, pairs);
    tree = l.tree;
    sectionId = l.sectionId;
    at = { ...at, index: undefined };
  }
  return tidy(mapSections(tree, (s) => {
    if (s.id !== sectionId) return s;
    const list = exercisesOf(s);
    const i = snapped(list, at.index ?? list.length);
    return { ...s, coach_workout_exercises: [...list.slice(0, i), ...rows, ...list.slice(i)] };
  }));
}

/** The rows as they travel: a run keeps its inner flags and ends unjoined; a single member leaves its run unjoined. */
function travelling(rows: TreeExercise[]): TreeExercise[] {
  return rows.map((r, i) => ({ ...r, superset_with_next: i < rows.length - 1 ? r.superset_with_next === true : false }));
}

/** A fresh copy of rows: every row and set a new id. */
function copied(rows: TreeExercise[], newId: NewId): TreeExercise[] {
  return rows.map((r) => ({ ...r, id: newId(), coach_workout_sets: setsOf(r).map((x) => ({ ...x, id: newId() })) }));
}

/**
 * Move (or copy) rows from one client copy to another — or within one (`from === to`). `rowIds` are moved in their order in
 * `from`. Returns the new tree for each workout touched (one or two).
 */
export function moveRows(from: WeekTree, to: WeekTree, rowIds: readonly string[], at: Landing, copy: boolean, newId: NewId): Map<string, WeekTree> {
  const order = sectionsOf(from).flatMap((s) => exercisesOf(s).map((e) => e.id)).filter((id) => rowIds.includes(id));
  const out = new Map<string, WeekTree>();
  if (copy) {
    const rows = travelling(order.map((id) => sectionsOf(from).flatMap(exercisesOf).find((e) => e.id === id)!));
    out.set(to.id, place(to, copied(rows, newId), at, newId));
    return out;
  }
  const { tree: left, rows } = takeRows(from, order);
  if (from.id === to.id) {
    out.set(from.id, place(left, travelling(rows), at, newId));
    return out;
  }
  out.set(from.id, tidy(left));
  out.set(to.id, place(to, travelling(rows), at, newId));
  return out;
}

/** An empty client copy for a day that has none — its first rows land in one implicit section. */
export function emptyTree(id: string, name: string): WeekTree {
  return { id, name, description: null, coach_workout_sections: [], updated_at: null };
}

/* ── the payload of `write_week_trees` ────────────────────────────────────────────────────────────────────────────── */

export type CreateEntry = { assignment_id: string; client_id: string; scheduled_date: string; name: string };

export type TreePayload = {
  workout_id: string;
  expected_updated_at: string | null;
  create?: CreateEntry;
  drop?: true;
  sections?: {
    id: string; position: number; name: string | null; format: string | null; section_type: string | null;
    description: string | null; is_implicit: boolean;
    exercises: {
      id: string; exercise_id: string | null; position: number; each_side: boolean; hold_until_lost: boolean; tempo: string | null;
      notes: string | null; superset_with_next: boolean;
      sets: { id: string; position: number; reps: number | null; weight_kg: number | null; time_seconds: number | null;
              rounds: number | null; rest_seconds: number | null; set_type: string; distance_m: number | null }[];
    }[];
  }[];
};

export function payloadOf(t: WeekTree, extra: { create?: CreateEntry } = {}): TreePayload {
  return {
    workout_id: t.id,
    expected_updated_at: extra.create === undefined ? t.updated_at ?? null : null,
    ...(extra.create === undefined ? {} : { create: extra.create }),
    sections: sectionsOf(t).map((s, si) => ({
      id: s.id, position: si, name: s.name, format: s.format, section_type: s.section_type, description: s.description,
      is_implicit: s.is_implicit === true,
      exercises: exercisesOf(s).map((e, ei) => ({
        id: e.id, exercise_id: e.exercise_id, position: ei, each_side: e.each_side === true, hold_until_lost: e.hold_until_lost === true,
        tempo: e.tempo, notes: e.notes, superset_with_next: e.superset_with_next === true,
        sets: setsOf(e).map((x, k) => ({
          id: x.id, position: k, reps: x.reps, weight_kg: wireNumber(x.weight_kg), time_seconds: x.time_seconds, rounds: x.rounds,
          rest_seconds: x.rest_seconds, set_type: x.set_type ?? "regular", distance_m: wireNumber(x.distance_m ?? null),
        })),
      })),
    })),
  };
}

export function dropPayload(workoutId: string, expected: string | null): TreePayload {
  return { workout_id: workoutId, expected_updated_at: expected, drop: true };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * STAGE 2 (2026-10-19, `docs/WEEK-EDITOR-STAGE2-PLAN.md`): a library pick dropped at a place, several rows from several days
 * moved or copied as one, and one line written on the same exercise across the week. Still pure, still ids kept.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

/** A library pick placed where it was dropped (between two rows, a section's top) — or at the end when no place is named. */
export function addExerciseAt(t: WeekTree, pick: LibraryPick, at: Landing, newId: NewId): { tree: WeekTree; rowId: string } {
  const row = seededRow(pick, newId);
  return { tree: place(t, [row], at, newId), rowId: row.id };
}

/**
 * Rows picked on SEVERAL client copies, moved (or copied) to one landing as ONE change. `sources` are in week order (the day,
 * then the workout's place in it); each source's picked rows go in their order there, so the rows land together in week order.
 * The three flags per source (`takeRows` + `travelling`, as one row's move). Returns every tree touched.
 */
export function moveMany(sources: readonly WeekTree[], picks: ReadonlyMap<string, readonly string[]>, to: WeekTree, at: Landing,
                         copy: boolean, newId: NewId): Map<string, WeekTree> {
  const out = new Map<string, WeekTree>();
  let target = to;
  const rows: TreeExercise[] = [];
  for (const src of sources) {
    const want = picks.get(src.id) ?? [];
    if (want.length === 0) continue;
    const from = src.id === to.id ? target : src;
    const order = sectionsOf(from).flatMap((s) => exercisesOf(s).map((e) => e.id)).filter((id) => want.includes(id));
    if (copy) {
      const found = order.map((id) => sectionsOf(from).flatMap(exercisesOf).find((e) => e.id === id)!);
      rows.push(...copied(travelling(found), newId));
      continue;
    }
    const { tree: left, rows: taken } = takeRows(from, order);
    rows.push(...travelling(taken));
    if (src.id === to.id) target = left; else out.set(src.id, tidy(left));
  }
  out.set(to.id, place(target, rows, at, newId));
  return out;
}

/** The ISO Monday ("YYYY-MM-DD") of a day key — the week a day belongs to. */
export function mondayOf(dayKey: string): string {
  const d = new Date(`${dayKey}T00:00:00Z`);
  const back = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - back);
  return d.toISOString().slice(0, 10);
}

/** Where else this week the SAME library exercise sits WITH THE SAME LINE, on workouts that can change: `candidates` are the
 *  week's untrained copies; `line` is this row's line BEFORE the edit (`showLine(lineFieldsOf(row))`); `canSay` drops a row whose
 *  sets one line cannot say (per-set rests, rounds) — writing a line there would lose them.
 *  🔴 A DAY WHOSE LINE DIFFERS IS NEVER OFFERED (Fable 2026-10-22): Saturday's 3×5 heavy beside Wednesday's 4×8 is a coach's
 *  decision, and "Same on Sat" would overwrite it with one tick. Only a day that already said exactly this line follows it. */
export function sameExerciseElsewhere(
  candidates: readonly { workoutId: string; dayKey: string; tree: WeekTree }[], exerciseId: string | null, exceptRowId: string,
  line: string, canSay: (e: TreeExercise) => boolean,
): { workoutId: string; rowId: string; dayKey: string }[] {
  if (exerciseId === null) return [];
  const out: { workoutId: string; rowId: string; dayKey: string }[] = [];
  for (const c of candidates) {
    for (const e of sectionsOf(c.tree).flatMap(exercisesOf)) {
      if (e.id === exceptRowId || e.exercise_id !== exerciseId || !canSay(e)) continue;
      if (showLine(lineFieldsOf(e)) !== line) continue;
      out.push({ workoutId: c.workoutId, rowId: e.id, dayKey: c.dayKey });
    }
  }
  return out;
}

/** One line written on several rows (its own and the same exercise elsewhere): every touched tree, each written once. */
export function applyLineMany(treeOf: (workoutId: string) => WeekTree | undefined,
                              rows: readonly { workoutId: string; rowId: string }[], f: LineFields, newId: NewId): Map<string, WeekTree> {
  const out = new Map<string, WeekTree>();
  for (const r of rows) {
    const t = out.get(r.workoutId) ?? treeOf(r.workoutId);
    if (t === undefined) continue;
    out.set(r.workoutId, applyLine(t, r.rowId, f, newId));
  }
  return out;
}
